import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BackLink } from '../../components/BackLink'
import { useBuilds } from '../../hooks/useBuilds'
import { usePrints } from '../../hooks/usePrints'
import { useFilamentSpools } from '../../hooks/useFilamentSpools'
import { useSettings } from '../../hooks/useSettings'
import { usePrinters } from '../../hooks/usePrinters'
import { supabase } from '../../lib/supabase'
import {
  filamentCostForUsage,
  printerTotalCostPerHour,
  printerElectricalCostPerHour,
} from '../../lib/pricing'
import { formatMoney } from '../../lib/format'
import { useQueryClient } from '@tanstack/react-query'

// Pick readable text color over an arbitrary hex background.
function isLightHex(hex: string): boolean {
  const h = hex.replace('#', '')
  if (h.length !== 6) return false
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 160
}

export default function BuildBulkFillPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: builds } = useBuilds()
  const { data: prints } = usePrints()
  const { data: spools } = useFilamentSpools()
  const { data: settings } = useSettings()
  const { data: printers } = usePrinters()

  const build = builds?.find((b) => b.id === id)
  const buildPrints = useMemo(
    () => (prints ?? []).filter((p) => p.build_id === id).sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [prints, id],
  )

  // Picked spool ids in order of selection.
  const [pickedSpoolIds, setPickedSpoolIds] = useState<string[]>([])
  // grams[`${printId}|${spoolId}`] = string
  const [grams, setGrams] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  // Pre-load existing usage rows so editing an already-filled build works too.
  useEffect(() => {
    if (buildPrints.length === 0) return
    let cancelled = false
    const printIds = buildPrints.map((p) => p.id)
    supabase
      .from('print_filament_usage')
      .select('print_id, spool_id, grams_used')
      .in('print_id', printIds)
      .then(({ data }) => {
        if (cancelled || !data) return
        const seedGrams: Record<string, string> = {}
        const seedPicked = new Set<string>()
        for (const u of data) {
          seedGrams[`${u.print_id}|${u.spool_id}`] = String(u.grams_used)
          seedPicked.add(u.spool_id)
        }
        if (Object.keys(seedGrams).length > 0) setGrams(seedGrams)
        if (seedPicked.size > 0) setPickedSpoolIds(Array.from(seedPicked))
      })
    return () => {
      cancelled = true
    }
  }, [buildPrints])

  const spoolById = useMemo(
    () => new Map((spools ?? []).map((s) => [s.id, s])),
    [spools],
  )
  const printer = (printers ?? []).find((p) => p.id === buildPrints[0]?.printer_id)
  const efficiency = settings?.material_efficiency_factor ?? 1.1
  const machineCostPerHour =
    printer && settings ? printerTotalCostPerHour(printer, settings) : 0
  const electricityCostPerHour =
    printer && settings ? printerElectricalCostPerHour(printer, settings) : 0
  const laborRatePerHour = settings ? Number(settings.labor_hourly_rate) : 0

  // Roll-up: total grams + cost across the build
  const totals = useMemo(() => {
    let totalGrams = 0
    let filamentCost = 0
    let machineCost = 0
    for (const p of buildPrints) {
      machineCost += Number(p.print_time_hours) * machineCostPerHour
      for (const sid of pickedSpoolIds) {
        const v = Number(grams[`${p.id}|${sid}`])
        if (!v || isNaN(v)) continue
        totalGrams += v
        const spool = spoolById.get(sid)
        if (spool) {
          filamentCost += filamentCostForUsage(v, spool, Number(efficiency))
        }
      }
    }
    return { totalGrams, filamentCost, machineCost, total: filamentCost + machineCost }
  }, [buildPrints, pickedSpoolIds, grams, spoolById, machineCostPerHour, efficiency])

  if (!build) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <BackLink fallback="/prints" />
        <p>Build not found.</p>
      </div>
    )
  }

  const togglePickSpool = (spoolId: string) => {
    setPickedSpoolIds((prev) =>
      prev.includes(spoolId) ? prev.filter((id) => id !== spoolId) : [...prev, spoolId],
    )
  }

  const setCellGrams = (printId: string, spoolId: string, v: string) => {
    setGrams((g) => ({ ...g, [`${printId}|${spoolId}`]: v }))
  }

  const handleSave = async () => {
    setSaving(true)
    setErrorMsg('')
    setSavedMsg('')
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const affectedSpoolIds = new Set<string>(pickedSpoolIds)

      for (const p of buildPrints) {
        // Wipe existing usage rows for this print.
        // (Collect prior spool ids so we can also reconcile those — they may
        // not be in pickedSpoolIds if the user un-picked them.)
        const { data: priorRows } = await supabase
          .from('print_filament_usage')
          .select('spool_id')
          .eq('print_id', p.id)
        for (const r of priorRows ?? []) affectedSpoolIds.add(r.spool_id)
        await supabase.from('print_filament_usage').delete().eq('print_id', p.id)

        // Insert the new usage rows for this print.
        const rows: Array<{
          user_id: string
          print_id: string
          spool_id: string
          grams_used: number
          cost_recognized: number
        }> = []
        let printFilamentCost = 0
        for (const sid of pickedSpoolIds) {
          const v = Number(grams[`${p.id}|${sid}`])
          if (!v || isNaN(v) || v <= 0) continue
          const spool = spoolById.get(sid)
          if (!spool) continue
          const cost = filamentCostForUsage(v, spool, Number(efficiency))
          rows.push({
            user_id: user.id,
            print_id: p.id,
            spool_id: sid,
            grams_used: v,
            cost_recognized: cost,
          })
          printFilamentCost += cost
        }
        if (rows.length > 0) {
          const { error: insErr } = await supabase.from('print_filament_usage').insert(rows)
          if (insErr) throw insErr
        }

        // Recompute every cached cost field — keeping electricity_cost and
        // labor_cost in sync with the source data, not stale at $0 from
        // initial print creation.
        const hours = Number(p.print_time_hours)
        const printMachineCost = hours * machineCostPerHour
        const printElectricityCost = hours * electricityCostPerHour
        const printLaborCost = (Number(p.labor_minutes) / 60) * laborRatePerHour
        const printTotalCost = printFilamentCost + printMachineCost + printLaborCost
        await supabase
          .from('prints')
          .update({
            filament_cost_total: printFilamentCost,
            machine_cost: printMachineCost,
            electricity_cost: printElectricityCost,
            labor_cost: printLaborCost,
            total_cost: printTotalCost,
          })
          .eq('id', p.id)
      }

      // Reconcile all affected spools' current weight.
      for (const sid of affectedSpoolIds) {
        const { data: allUsages } = await supabase
          .from('print_filament_usage')
          .select('grams_used')
          .eq('spool_id', sid)
        const used = (allUsages ?? []).reduce((sum, u) => sum + Number(u.grams_used), 0)
        const spool = spoolById.get(sid)
        if (!spool) continue
        await supabase
          .from('filament_spools')
          .update({ current_weight_grams: spool.original_weight_grams - used })
          .eq('id', sid)
      }

      // Invalidate cached queries so the rest of the app refreshes.
      queryClient.invalidateQueries({ queryKey: ['prints'] })
      queryClient.invalidateQueries({ queryKey: ['filament_spools'] })

      setSavedMsg('Saved — filament recorded across all plates.')
      setTimeout(() => navigate('/prints'), 1000)
    } catch (e) {
      setErrorMsg((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const activeSpools = (spools ?? []).filter((s) => s.status === 'in-use')

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 720, paddingBottom: 120 }}>
      <BackLink fallback="/prints" />
      <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, marginBottom: 4 }}>Bulk fill filament</h1>
      <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        {build.name ?? `Build ${build.id.slice(0, 6)}`} · {buildPrints.length} plate{buildPrints.length === 1 ? '' : 's'}
      </div>

      <section style={{ marginBottom: 'var(--space-4)' }}>
        <div style={labelStyle}>1. Pick the spools you used on this build</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {activeSpools.map((s) => {
            const picked = pickedSpoolIds.includes(s.id)
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => togglePickSpool(s.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  background: picked ? 'var(--color-primary-soft)' : 'var(--color-surface)',
                  border: picked ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: s.color_hex ?? '#999',
                    border: '1px solid var(--color-border)',
                  }}
                />
                <span>{s.brand ? `${s.brand.trim()} ${s.color}` : s.color}</span>
                <span style={{ color: 'var(--color-text-faint)', fontSize: 10 }}>
                  {s.current_weight_grams}g
                </span>
                {picked && <span style={{ color: 'var(--color-primary-text)', fontWeight: 700 }}>✓</span>}
              </button>
            )
          })}
        </div>
      </section>

      <section>
        <div style={labelStyle}>2. Enter grams per plate</div>
        {pickedSpoolIds.length === 0 ? (
          <div style={emptyStyle}>Pick at least one spool above to start filling rows.</div>
        ) : (
          <div style={{ marginTop: 'var(--space-2)' }}>
            {/* Header row showing spool color swatches */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `minmax(120px, 1fr) repeat(${pickedSpoolIds.length}, 70px)`,
                gap: 4,
                padding: '4px 8px',
                fontSize: 10,
                color: 'var(--color-text-muted)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              <div>Plate</div>
              {pickedSpoolIds.map((sid) => {
                const s = spoolById.get(sid)
                const c = s?.color_hex ?? '#999'
                const fg = isLightHex(c) ? '#000' : '#fff'
                return (
                  <div key={sid} style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: c,
                        border: '1px solid var(--color-border)',
                      }}
                      title={s ? `${s.brand ?? ''} ${s.color}` : ''}
                    />
                    <span style={{ color: fg === '#000' ? 'var(--color-text-muted)' : 'var(--color-text-muted)' }}>g</span>
                  </div>
                )
              })}
            </div>
            {buildPrints.map((p) => (
              <div
                key={p.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: `minmax(120px, 1fr) repeat(${pickedSpoolIds.length}, 70px)`,
                  gap: 4,
                  alignItems: 'center',
                  padding: '6px 8px',
                  background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 6,
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.description || `Plate`}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>
                    {p.print_date} · {p.print_time_hours}h
                  </div>
                </div>
                {pickedSpoolIds.map((sid) => (
                  <input
                    key={sid}
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={grams[`${p.id}|${sid}`] ?? ''}
                    onChange={(e) => setCellGrams(p.id, sid, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text)',
                      fontSize: 'var(--font-size-sm)',
                      fontVariantNumeric: 'tabular-nums',
                      textAlign: 'right',
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bottom sticky save bar */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'var(--nav-height-mobile)',
          background: 'var(--color-surface)',
          borderTop: '1px solid var(--color-border)',
          padding: 'var(--space-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          zIndex: 20,
        }}
      >
        <div style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>
          <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {totals.totalGrams}g · {formatMoney(totals.total)}
          </div>
          <div style={{ fontSize: 10, color: 'var(--color-text-faint)' }}>
            filament {formatMoney(totals.filamentCost)} · machine {formatMoney(totals.machineCost)}
          </div>
        </div>
        <button
          type="button"
          disabled={saving || pickedSpoolIds.length === 0}
          onClick={handleSave}
          style={{
            padding: '10px 18px',
            background: saving ? 'var(--color-text-faint)' : 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 700,
            fontSize: 'var(--font-size-sm)',
            cursor: saving ? 'default' : 'pointer',
            opacity: pickedSpoolIds.length === 0 ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save all'}
        </button>
      </div>

      {savedMsg && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(var(--nav-height-mobile) + 70px)',
            background: 'var(--color-success-soft)',
            color: 'var(--color-success-text)',
            border: '1px solid var(--color-success)',
            padding: '8px 16px',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            zIndex: 30,
          }}
        >
          ✓ {savedMsg}
        </div>
      )}
      {errorMsg && (
        <div
          style={{
            color: 'var(--color-danger-text)',
            fontSize: 'var(--font-size-sm)',
            marginTop: 'var(--space-3)',
          }}
        >
          {errorMsg}
        </div>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}

const emptyStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  textAlign: 'center',
  background: 'var(--color-surface)',
  color: 'var(--color-text-muted)',
  borderRadius: 'var(--radius-md)',
  marginTop: 'var(--space-2)',
  fontSize: 'var(--font-size-sm)',
}
